import React from 'react';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomTextArea from 'components/Fileds/CustomTextArea';
import CustomCheckbox from 'components/Fileds/CustomCheckbox';
import CustomAutocomplete from 'components/Fileds/CustomAutocomplete';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import { Box, useTheme } from '@mui/material';
import { useGetChefsListsQuery } from 'pages/customers/services';

const MiscItemSchema = Yup.object().shape({
  name: Yup.string().trim().required('Item Name is required'),
  price: Yup.mixed()
    .test('is-valid-price', 'Price must be a number', (value) => {
      if (value === '' || value == null) return true;
      const num = Number(value);
      return !Number.isNaN(num) && num >= 0;
    }),
  notes: Yup.string().nullable(),
  cookingRequired: Yup.boolean(),
  chef: Yup.object()
    .nullable()
    .test('chef-required', 'Chef is required when cooking is required', function (value) {
      const { cookingRequired } = this.parent;
      if (cookingRequired && !value) return false;
      return true;
    }),
});

const AddMiscItemModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const { data: chefsData = [], isLoading: loadingChefs } = useGetChefsListsQuery();

  const chefOptions = chefsData?.data?.records?.map((c) => ({
    // label: c.full_name || c.username || c.name,
    label: c.station_name ? `${c.full_name || c.username || c.name} - (${c.station_name})` : c.full_name || c.username || c.name,
    value: c.id,
    station_id: c.station_id || c.station?.id || c.stationId || (Array.isArray(c.stations) && c.stations[0]?.id) || null,
  })) || [];

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title="Add Misc."
      initialValues={{ name: '', price: '', notes: '', cookingRequired: false, chef: null }}
      validationSchema={MiscItemSchema}
      onSubmit={onSubmit}
      submitLabel="Save"
      requiredFields={['name']}
      showCancel={false}
      cancelLabel="Cancel"
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, validateForm }) => (
        <>
          <CustomTypography
            label="Item Name*"
            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
          />
          <CustomTextField
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            placeholder="Enter Item Name"
            error={touched.name && Boolean(errors.name)}
            helperText={touched.name && errors.name}
          />

          <CustomTypography
            label="Item Price"
            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
          />

          <CustomTextField
            name="price"
            value={values.price}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
              setFieldValue('price', val);
            }}
            onBlur={handleBlur}
            fullWidth
            placeholder="Enter Item Price"
            error={touched.price && Boolean(errors.price)}
            helperText={touched.price && errors.price}
          />

          <CustomTypography
            label="Notes"
            sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
          />
          <CustomTextArea
            name="notes"
            value={values.notes}
            onChange={handleChange}
            onBlur={handleBlur}
            minRows={3}
            placeholder="Optional notes..."
            error={touched.notes && Boolean(errors.notes)}
            helperText={touched.notes && errors.notes}
          />

          {/* Cooking Required Checkbox */}
          <Box>
            <CustomCheckbox
              label="Does this item require cooking?"
              checked={values.cookingRequired}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setFieldValue('cookingRequired', isChecked);
                if (!isChecked) {
                  setFieldValue('chef', null);
                }
                setTimeout(() => validateForm(), 0);
              }}
            />
          </Box>

          {/* Chef Dropdown - shown only when cooking is required */}
          {values.cookingRequired && (
            <Box sx={{ mt: 1 }}>
              <CustomTypography
                label="Select Chef*"
                sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
              />
              <CustomAutocomplete
                name="chef"
                options={chefOptions}
                value={values.chef}
                onChange={(_, val) => setFieldValue('chef', val)}
                onBlur={handleBlur}
                placeholder={loadingChefs ? "Loading chefs..." : "Select a Chef"}
                error={touched.chef && Boolean(errors.chef)}
                helperText={touched.chef && errors.chef}
                loading={loadingChefs}
              />
            </Box>
          )}
        </>
      )}
    </GenericDialog>
  );
};

export default AddMiscItemModal;
